import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PrivateLayout from "../../layouts/PrivateLayout";
import {
  fetchSoloDuoStandings,
  fetchSoloDuoRegistrationRuns,
  fetchUserCharacters,
  cancelSoloDuoRegistrationById,
  adminDeleteSoloDuoRegistration,
  registerSoloDuoLadder,
} from "../../api/api";
import useDecodedToken from "../../utils/useDecodedToken";
import { useSelectedCharacter } from "../../context/SelectedCharacterContext";
import SendDuoInvitationModal from "../../components/modals/SendDuoInvitationModal";

function SoloDuoStandings() {
  const backgroundImage = "/ww-wallpaper.webp";
  const navigate = useNavigate();
  const { token, decodedJwt } = useDecodedToken();
  const { setSelectedCharacter } = useSelectedCharacter();

  const isAdmin = decodedJwt?.role === "admin";
  console.log(
    "[Standings] Logged-in account has Admin role:",
    isAdmin,
    "(role:",
    decodedJwt?.role ?? "none",
    ")",
  );

  const [standingsData, setStandingsData] = useState(null);
  const [myCharacters, setMyCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [unregisteringId, setUnregisteringId] = useState(null);
  const [successToast, setSuccessToast] = useState("");

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState("");

  const [runsModalRegistrationId, setRunsModalRegistrationId] = useState(null);
  const [runsModalEntry, setRunsModalEntry] = useState(null);
  const [runsModalRuns, setRunsModalRuns] = useState([]);
  const [runsModalLoading, setRunsModalLoading] = useState(false);
  const [runsModalError, setRunsModalError] = useState("");

  const [sendInvitationModalOpen, setSendInvitationModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const [standings, characters] = await Promise.all([
        fetchSoloDuoStandings(token, { limit: 50 }),
        fetchUserCharacters(token),
      ]);
      setStandingsData(standings);
      setMyCharacters(characters || []);
      console.log("[Standings] standings[0]:", standings?.standings?.[0]);
      console.log("[Standings] characters[0]:", (characters || [])[0]);
    } catch (error) {
      console.error("Error fetching Solo/Duo standings:", error);
      setErrorMessage(error.message || "Failed to load standings.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const characterMap = useMemo(() => {
    const map = {};
    myCharacters.forEach((c) => {
      // index by name+realm and also by name alone as fallback
      const realmName = (
        c.realm?.name ||
        c.realm?.slug ||
        c.realm ||
        ""
      ).toLowerCase();
      const key = `${c.name?.toLowerCase()}-${realmName}`;
      map[key] = c;
      // also store by name-only key as fallback
      const nameKey = `name:${c.name?.toLowerCase()}`;
      if (!map[nameKey]) map[nameKey] = c;
    });
    return map;
  }, [myCharacters]);

  const registeredKeys = useMemo(() => {
    const keys = new Set();
    standingsData?.standings?.forEach((e) => {
      keys.add(
        `${e.characterName?.toLowerCase()}-${e.characterRealm?.toLowerCase()}`,
      );
    });
    return keys;
  }, [standingsData]);

  const unregisteredCharacters = useMemo(
    () =>
      myCharacters.filter((c) => {
        const key = `${c.name?.toLowerCase()}-${c.realm?.name?.toLowerCase()}`;
        return !registeredKeys.has(key);
      }),
    [myCharacters, registeredKeys],
  );

  const getCharacterData = (entry) => {
    const key = `${entry.characterName?.toLowerCase()}-${entry.characterRealm?.toLowerCase()}`;
    // Try exact name+realm match first, then name-only fallback
    return (
      characterMap[key] ||
      characterMap[`name:${entry.characterName?.toLowerCase()}`] ||
      null
    );
  };

  const isMyEntry = (entry) => !!getCharacterData(entry);

  const handleUnregister = useCallback(
    async (registrationId) => {
      if (!token) return;
      setUnregisteringId(registrationId);
      try {
        await cancelSoloDuoRegistrationById(token, registrationId);
        setSuccessToast("Successfully unregistered from the ladder.");
        setTimeout(() => setSuccessToast(""), 4000);
        await loadData();
      } catch (error) {
        console.error("Error unregistering:", error);
        setErrorMessage(
          error.message || "Failed to unregister. Please try again.",
        );
      } finally {
        setUnregisteringId(null);
      }
    },
    [token, loadData],
  );

  const handleAdminRemove = useCallback(
    async (registrationId) => {
      if (!token || !isAdmin) return;
      setUnregisteringId(registrationId);
      try {
        await adminDeleteSoloDuoRegistration(token, registrationId);
        setSuccessToast("Registration removed.");
        setTimeout(() => setSuccessToast(""), 4000);
        await loadData();
      } catch (error) {
        console.error("Error removing registration:", error);
        setErrorMessage(
          error.message || "Failed to remove registration. Please try again.",
        );
      } finally {
        setUnregisteringId(null);
      }
    },
    [token, isAdmin, loadData],
  );

  const handleRegisterWithCharacter = useCallback(
    async (character) => {
      if (!token || !character) return;
      setIsSubmitting(true);
      setRegisterError("");
      try {
        const ladderCharacterId = character.characterId || character.id;
        await registerSoloDuoLadder(token, {
          type: "solo",
          characterId: String(ladderCharacterId),
          partnerCharacterId: null,
        });
        setSelectedCharacter(character);
        setIsRegisterModalOpen(false);
        setSuccessToast(`${character.name} registered for the Solo ladder.`);
        setTimeout(() => setSuccessToast(""), 4000);
        await loadData();
      } catch (error) {
        console.error("Error registering:", error);
        setRegisterError(
          error.message || "Failed to register. Please try again.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [token, setSelectedCharacter, loadData],
  );

  const hasStandings =
    standingsData &&
    Array.isArray(standingsData.standings) &&
    standingsData.standings.length > 0;

  const getRankStyle = (rank) => {
    if (rank === 1) return "text-yellow-400 font-bold";
    if (rank === 2) return "text-gray-300 font-bold";
    if (rank === 3) return "text-amber-600 font-bold";
    return "text-white font-semibold text-sm";
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  const getRowBg = (rank) => {
    if (rank === 1) return "bg-yellow-500/10 border-yellow-500/30";
    if (rank === 2) return "bg-slate-400/20 border-slate-300/40";
    if (rank === 3) return "bg-amber-600/10 border-amber-600/20";
    return "bg-white/5 border-white/10 hover:bg-white/10";
  };

  const openRunsModal = useCallback(
    async (entry) => {
      if (!token) return;
      setRunsModalRegistrationId(entry.registrationId);
      setRunsModalEntry(entry);
      setRunsModalRuns([]);
      setRunsModalError("");
      setRunsModalLoading(true);
      try {
        const data = await fetchSoloDuoRegistrationRuns(
          token,
          entry.registrationId,
        );
        setRunsModalRuns(data.runs || []);
      } catch (err) {
        setRunsModalError(err.message || "Failed to load runs.");
      } finally {
        setRunsModalLoading(false);
      }
    },
    [token],
  );

  const closeRunsModal = useCallback(() => {
    setRunsModalRegistrationId(null);
    setRunsModalEntry(null);
    setRunsModalRuns([]);
    setRunsModalError("");
  }, []);

  const formatRunDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center text-white bg-black/50 px-4 py-10 min-h-full">
        <div className="w-full max-w-7xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg mb-2">
              Solo/Duo Standings
            </h1>
            <p className="text-gray-300 text-base md:text-lg">
              Current Solo and Duo ladder rankings
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mb-6">
            <button
              type="button"
              onClick={() => {
                setRegisterError("");
                setIsRegisterModalOpen(true);
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-blue-600 hover:bg-blue-500 transition-colors shadow-lg"
            >
              <i className="fas fa-plus text-xs"></i>
              Register new character
            </button>
            <button
              type="button"
              onClick={() => navigate("/solo-duo-ranking/rules")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-green-600 hover:bg-green-500 transition-colors shadow-lg"
            >
              <i className="fas fa-book text-xs"></i>
              Rules
            </button>
            <button
              type="button"
              onClick={() => setSendInvitationModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-purple-600 hover:bg-purple-500 transition-colors shadow-lg"
            >
              <i className="fas fa-paper-plane text-xs"></i>
              Send Invitation
            </button>
          </div>

          {/* Error */}
          {errorMessage && (
            <div className="mb-6 bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
              {errorMessage}
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-300 text-sm">Loading standings...</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !errorMessage && standingsData && !hasStandings && (
            <div className="text-center py-20">
              <i className="fas fa-trophy text-5xl text-gray-600 mb-4 block"></i>
              <p className="text-gray-400 text-lg">
                No active standings at the moment.
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Check back once the ladder season begins.
              </p>
            </div>
          )}

          {/* Standings Table */}
          {!isLoading && !errorMessage && hasStandings && (
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              {/* Table Header */}
              <div className="grid grid-cols-[60px_1fr_1fr_100px_80px_120px] bg-white/90 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-gray-500 border-b border-gray-200">
                <div className="text-center">Rank</div>
                <div>Character</div>
                <div>Realm</div>
                <div className="text-right">Score</div>
                <div></div>
                <div></div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-white/5">
                {standingsData.standings.map((entry) => (
                  <div
                    key={entry.registrationId}
                    className={`grid grid-cols-[60px_1fr_1fr_100px_80px_120px] items-center px-4 py-3.5 border-l-2 transition-colors ${getRowBg(entry.rank)}`}
                  >
                    {/* Rank */}
                    <div
                      className={`text-center text-2xl ${getRankStyle(entry.rank)}`}
                    >
                      {getRankIcon(entry.rank)}
                    </div>

                    {/* Character Name */}
                    {(() => {
                      const charData = getCharacterData(entry);
                      const avatarUrl = charData?.mediaLinks?.[0]?.value;
                      const className = charData?.playable_class?.name;
                      const isDuo = entry.type === "duo" && entry.partnerName;
                      const partnerName = entry.partnerName;

                      const partnerKey = `${partnerName?.toLowerCase()}-${entry.partnerRealm?.toLowerCase()}`;
                      const partnerNameKey = `name:${partnerName?.toLowerCase()}`;
                      const partnerData =
                        characterMap[partnerKey] ||
                        characterMap[partnerNameKey] ||
                        null;
                      const partnerAvatarUrl =
                        partnerData?.mediaLinks?.[0]?.value ||
                        entry.partnerAvatarUrl ||
                        null;

                      if (isDuo) {
                        return (
                          <div className="flex items-center gap-2">
                            {/* Avatar stack */}
                            <div className="relative flex-shrink-0 w-14 h-10">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={entry.characterName}
                                  className="w-9 h-9 rounded-full border-2 border-white/20 object-cover absolute left-0 top-0"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-blue-600/40 border-2 border-blue-500/40 flex items-center justify-center absolute left-0 top-0">
                                  <i className="fas fa-user text-blue-300 text-xs"></i>
                                </div>
                              )}
                              {partnerAvatarUrl ? (
                                <img
                                  src={partnerAvatarUrl}
                                  alt={partnerName}
                                  className="w-9 h-9 rounded-full border-2 border-purple-400/60 object-cover absolute left-5 top-0"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-purple-600/40 border-2 border-purple-500/40 flex items-center justify-center absolute left-5 top-0">
                                  <i className="fas fa-user text-purple-300 text-xs"></i>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-white text-sm leading-tight">
                                {entry.characterName}
                                <span className="text-gray-400 font-normal mx-1.5">
                                  &
                                </span>
                                {partnerName}
                              </p>
                              <span className="text-xs text-gray-400">
                                {className ? `${className} · Duo` : "Duo"}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="flex items-center gap-3">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={entry.characterName}
                              className="w-10 h-10 rounded-full border-2 border-white/20 flex-shrink-0 object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-600/40 border border-blue-500/40 flex items-center justify-center flex-shrink-0">
                              <i className="fas fa-user text-blue-300 text-xs"></i>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-white text-sm leading-tight">
                              {entry.characterName}
                            </p>
                            <span className="text-xs text-gray-400 capitalize">
                              {className
                                ? `${className} · ${entry.type}`
                                : entry.type}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Realm */}
                    {(() => {
                      const isDuo = entry.type === "duo" && entry.partnerName;
                      const partnerRealm = entry.partnerRealm;
                      return (
                        <div className="text-gray-300 text-sm truncate pr-2">
                          {isDuo &&
                          partnerRealm &&
                          partnerRealm !== entry.characterRealm
                            ? `${entry.characterRealm} / ${partnerRealm}`
                            : entry.characterRealm}
                        </div>
                      );
                    })()}

                    {/* Score */}
                    <div className="text-right">
                      <span className="text-base font-bold text-white">
                        {entry.score.toLocaleString()}
                      </span>
                    </div>

                    {/* Runs */}
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => openRunsModal(entry)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-yellow-600/80 hover:bg-yellow-500 text-white transition-colors whitespace-nowrap"
                      >
                        <i className="fas fa-running text-xs"></i>
                        Runs
                      </button>
                    </div>

                    {/* Unregister (own) or Remove (admin on others) */}
                    <div className="flex justify-end">
                      {isMyEntry(entry) && (
                        <button
                          type="button"
                          onClick={() => handleUnregister(entry.registrationId)}
                          disabled={unregisteringId === entry.registrationId}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {unregisteringId === entry.registrationId ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-times"></i>
                          )}
                          Unregister
                        </button>
                      )}
                      {isAdmin && !isMyEntry(entry) && (
                        <button
                          type="button"
                          onClick={() =>
                            handleAdminRemove(entry.registrationId)
                          }
                          disabled={unregisteringId === entry.registrationId}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-600/80 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          title="Admin: remove this registration"
                        >
                          {unregisteringId === entry.registrationId ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-user-minus"></i>
                          )}
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="bg-white/90 px-4 py-2.5 text-right text-xs text-gray-500 border-t border-gray-200">
                {standingsData.total} participant
                {standingsData.total !== 1 ? "s" : ""} registered
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Register character modal */}
      {isRegisterModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md md:max-w-lg p-6 text-gray-900">
            <h2 className="text-xl font-bold mb-4 text-center">
              Choose character for Solo ladder
            </h2>

            {registerError && (
              <p className="mb-3 text-sm text-red-500 text-center">
                {registerError}
              </p>
            )}

            {unregisteredCharacters.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                All your characters are already registered.
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 mb-4">
                {unregisteredCharacters.map((character) => (
                  <li
                    key={character.id}
                    className="py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {character.mediaLinks?.[0] ? (
                        <img
                          src={character.mediaLinks[0].value}
                          alt={character.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <i className="fas fa-user text-gray-400"></i>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">
                          {character.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {character.playable_class?.name
                            ? `${character.playable_class.name} · ${character.realm?.name}`
                            : character.realm?.name}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRegisterWithCharacter(character)}
                      disabled={isSubmitting}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        "Use this character"
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsRegisterModalOpen(false)}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Runs modal */}
      {runsModalRegistrationId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50 p-4">
          <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-white/10">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">
                Runs
                {runsModalEntry && (
                  <span className="font-normal text-gray-400 ml-2">
                    — {runsModalEntry.characterName}
                    {runsModalEntry.partnerName
                      ? ` & ${runsModalEntry.partnerName}`
                      : ""}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={closeRunsModal}
                className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {runsModalLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-gray-400 text-sm">Loading runs...</p>
                </div>
              )}
              {!runsModalLoading && runsModalError && (
                <p className="text-red-400 text-sm text-center py-6">
                  {runsModalError}
                </p>
              )}
              {!runsModalLoading &&
                !runsModalError &&
                runsModalRuns.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-6">
                    No runs in the current season.
                  </p>
                )}
              {!runsModalLoading &&
                !runsModalError &&
                runsModalRuns.length > 0 && (
                  <div className="space-y-3">
                    {runsModalRuns.map((run) => (
                      <div
                        key={run.id}
                        className={`rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${
                          run.countsForScore
                            ? "bg-white/10 border border-white/10"
                            : "bg-white/5 border border-red-500/30"
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-white">
                              {run.dungeonName}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/30 text-blue-200 font-medium">
                              +{run.mythicLevel}
                            </span>
                            {run.countsForScore ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-300 font-medium">
                                Counts
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/30 text-red-300 font-medium">
                                Excluded
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-white/70">
                            Completed: {formatRunDate(run.completedAt)}
                          </span>
                          {!run.countsForScore && run.exclusionReason && (
                            <span className="text-xs text-red-300">
                              Reason: {run.exclusionReason}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 md:text-right">
                          <div>
                            <p className="text-xs text-white/70">Score</p>
                            <p className="text-lg font-bold text-yellow-300">
                              {run.score ?? "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Send Invitation modal */}
      <SendDuoInvitationModal
        token={token}
        isOpen={sendInvitationModalOpen}
        onClose={() => setSendInvitationModalOpen(false)}
        onSent={(label) => {
          setSuccessToast(`Invitation sent to ${label}!`);
          setTimeout(() => setSuccessToast(""), 4000);
        }}
      />

      {/* Success toast */}
      {successToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-emerald-600 text-white text-sm font-medium px-5 py-3.5 rounded-xl shadow-2xl animate-fade-in">
          <i className="fas fa-check-circle text-base"></i>
          {successToast}
        </div>
      )}
    </PrivateLayout>
  );
}

export default SoloDuoStandings;
